import { ZValidation } from "models/common/validation";
import { ZTooltip } from "./ZTooltip";
import { ZIcon } from "./ZIcon";
import { ZIconType } from "helpers/utility/iconMap";
import { ZButton } from "./ZButton";
import { NumberFormatValues, NumericFormat } from "react-number-format";
import { KeyCode } from "ag-grid-community";
import classNames from "classnames";
import { ZLabel } from "./ZLabel";

type ZNumberInputProps = {
    showPercentIconBefore?: boolean|null;
    value?: number | null;
    ref?: React.Ref<HTMLInputElement>;
    min?: number;
    max?: number;
    step?: number;
    showThousands?: boolean;
    type: "qty" | "price" | "percent" | "integer-percent" | "integer-price" | "decimal" | "aggrid-price";
    label?: React.ReactNode | string;
    placeholder?: string;
    disabled?: boolean;
    validation?: ZValidation;
    autoFocus?: boolean;
    id?: string;
    style?: React.CSSProperties;
    containerClassName?: string;
    containerStyle?: React.CSSProperties;
    onChange?: (value: number | null | undefined) => void;
    onBlur?: () => void;
};

export const ZNumberInput = ({ showPercentIconBefore, value, ref, min, max, step = 1, showThousands = true, type, label, placeholder, disabled, validation, autoFocus, id, style, containerClassName, containerStyle, onChange, onBlur }: ZNumberInputProps) => {
    // Enforce max on input change and handle undefined values
    const handleChange = (values: NumberFormatValues) => {
        const { floatValue } = values;
        if (floatValue != null) {
            if (max !== undefined && floatValue > max) {
                onChange && onChange(max);
            } else {
                onChange && onChange(floatValue);
            }
        } else {
            onChange && onChange(null);
        }
    };

    // Enforce min and max on blur
    const handleBlur = () => {
        if (value == null) {
            onBlur && onBlur();
            return;
        }
        
        let adjustedValue = Math.min(Math.max(value, min ?? value), max ?? value);
        onChange && onChange(adjustedValue);
        onBlur && onBlur();
    };

    // Increment value within max constraint
    const increment = () => {
        let newValue = (value || 0) + step;
        newValue = max !== undefined ? Math.min(newValue, max) : newValue;
        onChange && onChange(newValue);
    };

    // Decrement value within min constraint
    const decrement = () => {
        let newValue = (value || 0) - step;
        newValue = min !== undefined ? Math.max(newValue, min) : newValue;
        onChange && onChange(newValue);
    };

    function handleKeyDown(event: KeyboardEvent): void {
        switch(event.key) {
            case KeyCode.UP:
                event.preventDefault();
                increment();
                break;
            case KeyCode.DOWN:
                event.preventDefault();
                decrement();
                break;
        }
    }

    return (
        <div className={classNames("d-flex", "flex-column", containerClassName)} style={{...containerStyle}}>
            <ZLabel preventSelect>{label}</ZLabel>
            <div className={`d-inline-flex align-items-center rounded znumber-input${validation?.invalid ? ` invalid` : ``}${disabled ? ` disabled` : ``}`}>
                { ["price", "integer-price", "aggrid-price"].includes(type) && <ZIcon iconClassName="ps-1" icon={ZIconType.Price} />}
                { (showPercentIconBefore == true && ["percent", "integer-percent"].includes(type)) && <ZIcon iconClassName="ps-1" icon={ZIconType.Percent} />}
                <NumericFormat
                    ref={ref}
                    style={style}
                    id={id}
                    autoFocus={autoFocus}
                    tabIndex={0}
                    value={value}
                    thousandSeparator={showThousands && ','}
                    thousandsGroupStyle={'thousand'}
                    decimalScale={["qty", "integer-percent", "integer-price"].includes(type) ? 0 : 2}
                    fixedDecimalScale={type === "price"}
                    onValueChange={handleChange}
                    onBlur={handleBlur}
                    //@ts-ignore
                    onKeyDown={handleKeyDown}
                    step={step}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`znumber-input-inner ${["qty", "integer-percent", "percent", "aggrid-price"].includes(type) ? "ps-2" : ""} p-1 flex-grow`}
                ></NumericFormat>
                {validation?.invalid &&
                    <ZTooltip
                        enabled
                        text={validation.error}
                    >
                        <ZIcon iconClassName="pe-1" icon={ZIconType.Error} variant="danger" />
                    </ZTooltip>
                }
                
                { type === "qty" && <ZButton size="sm" tabIndex={-1} className="znumber-input-minus" buttonText="-" onClick={decrement}/>}
                { type === "qty" && <ZButton size="sm" tabIndex={-1} className="znumber-input-plus" buttonText="+" onClick={increment}/>}
            </div>
        </div>
    );
}